/* eslint-disable @altermaind/you-shall-not-pass */
import { FC, MouseEvent, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useStyle, useThemeValue, Svg, Asset } from '@altermaind/web/ui';
import { Claims, FeatureFlag, Roles, Routes } from '@/_shared/constants';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { getEmployeesMeCall } from '@/profile/state/api';
import { getTemplatesCall } from '@/surveys/state/api';
import { ClaimsComponent } from '../Claims/Claims';
import { useMenu } from './useMenu';

const POPOVER_GAP = 8;
const HOVER_CLOSE_DELAY = 100;

type ILink = {
  key: string;
  name: string;
  url?: string;
  icon?: string;
  subItems?: ILink[];
  ff?: FeatureFlag | FeatureFlag[] | null;
  ffMode?: 'every' | 'some';
  claim?: Claims | Claims[] | null;
  hasComingSoon?: boolean;
  roles?: Roles[];
};

type PopoverPosition = {
  top: number;
  left: number;
};

type Props = {
  handleMenuToggle?: () => void;
};

export const MenuLight: FC<Props> = ({ handleMenuToggle }) => {
  const t = useTranslations('menu');
  const xxsSpace = useThemeValue('space', 'xxs');

  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { menu } = getTemplatesCall.hook();
  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();

  const links: ILink[] = [
    {
      key: 'homepage',
      name: t('homepage'),
      url: Routes.Homepage,
      icon: 'dashboard',
      subItems: [],
      ff: FeatureFlag.DASHBOARD,
      claim: Claims.DASHBOARD_READ,
    },
    {
      key: 'myAssessments',
      name: t('myAssessments'),
      url: '#',
      icon: 'document',
      subItems: [
        ...(menu.length > 0
          ? menu.map(item => ({
              key: `myAssessments-${item.id}`,
              name: item.name,
              url: `${Routes.AssessmentDetail.replace(':templateId', item.id)}`,
              icon: 'document',
              ff: FeatureFlag.PROFILE,
              claim: Claims.PROFILE_READ,
              roles: [Roles.Manager, Roles.Employee],
            }))
          : []),
      ],
      ff: FeatureFlag.PROFILE,
      claim: Claims.PROFILE_READ,
      roles: [Roles.Manager, Roles.Employee],
    },
    {
      key: 'companyStructure',
      name: t('companyStructure'),
      url: '#',
      icon: 'company',
      subItems: [
        {
          key: 'departments',
          name: t('departments'),
          url: Routes.Departments,
          icon: 'team',
          ff: FeatureFlag.DEPARTMENTS,
          claim: Claims.DEPARTMENTS_READ,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
        {
          key: 'people',
          name: t('people'),
          url: Routes.People,
          icon: 'users',
          ff: FeatureFlag.PEOPLE,
          claim: Claims.PEOPLE_READ,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
      ],
      ff: FeatureFlag.ORGANIZATION,
      claim: Claims.ORGANIZATION_READ,
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
    {
      key: 'survey',
      name: t('survey'),
      url: Routes.Surveys,
      icon: 'document',
      subItems: [
        ...(menu.length > 0
          ? menu.map(item => ({
              key: `survey-${item.id}`,
              name: item.name,
              url: `${Routes.SurveyDetail.replace(':surveyId', item.id)}`,
              icon: 'document',
              ff: FeatureFlag.SURVEY,
              claim: Claims.SURVEYS_WRITE,
              roles: [Roles.Admin, Roles.HR_Delegate],
            }))
          : []),
      ],
      ff: FeatureFlag.SURVEY,
      claim: Claims.SURVEYS_WRITE,
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
    {
      key: 'training',
      name: t('training'),
      url: '#',
      icon: 'rocket',
      hasComingSoon: true,
      subItems: [
        {
          key: 'ai-playground',
          name: t('aiPlayground'),
          url: Routes.AiPlayground,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
        {
          key: 'training-soft-skills',
          name: t('trainingSoftSkills'),
          url: Routes.TrainingSoftSkills,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
        {
          key: 'training-hard-skills',
          name: t('trainingHardSkills'),
          url: Routes.TrainingHardSkills,
          icon: 'rocket',
          ff: FeatureFlag.TRAINING,
          claim: Claims.TRAINING_READ,
        },
      ],
      claim: Claims.TRAINING_READ,
    },
    hasManagerLeader
      ? {
          key: 'department',
          name: t('department'),
          url: Routes.Department,
          icon: 'users',
          hasComingSoon: false,
          subItems: [],
          claim: Claims.DEPARTMENT_READ,
          ff: FeatureFlag.DEPARTMENT,
          roles: [Roles.Manager],
        }
      : null,
    hasTeamLeader
      ? {
          key: 'department',
          name: t('team'),
          url: Routes.Team,
          icon: 'users',
          hasComingSoon: false,
          subItems: [],
          claim: Claims.DEPARTMENT_READ,
          ff: FeatureFlag.DEPARTMENT,
          roles: [Roles.Manager],
        }
      : null,
    {
      key: 'settings',
      name: t('settings'),
      url: Routes.Settings,
      icon: 'aspect',
      ff: [FeatureFlag.ASPECT, FeatureFlag.ORGANIZATION],
      ffMode: 'some',
      subItems: [],
      claim: [Claims.ASPECT_READ, Claims.ORGANIZATION_READ],
      roles: [Roles.Admin, Roles.HR_Delegate],
    } as ILink,
    {
      key: 'reports',
      name: t('reports'),
      url: '#',
      icon: 'docsRar',
      ff: FeatureFlag.REPORTS,
      subItems: [
        {
          key: 'reports-assessments',
          name: t('assessmentsReports'),
          url: Routes.ReportsAssessments,
          icon: 'departments',
          ff: FeatureFlag.REPORTS,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
        {
          key: 'reports-training',
          name: t('trainingReports'),
          url: Routes.ReportsTrainings,
          icon: 'users',
          ff: FeatureFlag.REPORTS,
          roles: [Roles.Admin, Roles.HR_Delegate],
        },
      ],
      roles: [Roles.Admin, Roles.HR_Delegate],
    },
  ].filter(menuItem => menuItem !== null);

  const openChatbot = () => {
    const chatbotIcon = document.querySelector(
      '[data-testid="chatbot-icon"]',
    ) as HTMLElement | null;
    if (chatbotIcon) {
      chatbotIcon.click();
    }
  };

  const { hasSubMenu, goTo, isPathMatch } = useMenu();

  const hasAccent = window.accentLight || window.accentDark;

  const hoverStyle = useStyle({
    backgroundColor: hasAccent ? 'accent/background' : 'grey/dark/background',
    borderRadius: 'l',
  });

  const colorDefault = useMemo(() => {
    if (hasAccent) {
      return 'accent';
    }
    return 'primary';
  }, [hasAccent]);

  const { isFeatureEnabled } = useFeatureFlags();
  const isChatBotEnabled = isFeatureEnabled(FeatureFlag.CHATBOT);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (index: number, event: MouseEvent<HTMLDivElement>) => {
    clearCloseTimeout();
    const rect = event.currentTarget.getBoundingClientRect();
    setPopoverPosition({
      top: rect.top,
      left: rect.right + POPOVER_GAP,
    });
    setHoveredItem(index);
  };

  const handleMouseLeave = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, HOVER_CLOSE_DELAY);
  };

  const handlePopoverMouseEnter = () => {
    clearCloseTimeout();
  };

  const handlePopoverMouseLeave = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, HOVER_CLOSE_DELAY);
  };

  const renderPopover = (link: ILink, index: number) => {
    if (hoveredItem !== index) return null;

    const hasSubItems = hasSubMenu(link);

    return ReactDOM.createPortal(
      <Box
        position='fixed'
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
          zIndex: 99999,
        }}
        backgroundColor='secondary/background'
        borderRadius='m'
        boxShadow='0 4px 20px rgba(0, 0, 0, 0.15)'
        p='xs'
        minWidth={180}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      >
        <Typography variant='h4' color='primary/text' mb={hasSubItems ? 'xxs' : undefined}>
          {link.name}
        </Typography>
        {hasSubItems && (
          <Box mt='xxs'>
            {link.subItems?.map(subLink => {
              const isSubLinkActive = isPathMatch(subLink.url, window.location.pathname);
              return (
                <ClaimsComponent
                  key={subLink.key}
                  featureFlag={subLink.ff || undefined}
                  featureFlagCheckMode={subLink.ffMode || undefined}
                  claimKey={subLink.claim || undefined}
                  roles={subLink.roles || undefined}
                  notCheckWriteOrRead
                >
                  <Box
                    py='xxs'
                    px='xs'
                    borderRadius='m'
                    style={{
                      cursor: 'pointer',
                      ...(isSubLinkActive ? hoverStyle : {}),
                    }}
                    onClick={() => {
                      goTo(subLink.url, index);
                      if (handleMenuToggle) handleMenuToggle();
                      setHoveredItem(null);
                    }}
                    data-testid={`submenu-light-item-${subLink.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Box display='flex' alignItems='center' style={{ gap: xxsSpace }}>
                      {subLink.icon && (
                        <Svg
                          name={subLink.icon}
                          stroke={isSubLinkActive ? colorDefault : 'grey/light/background'}
                          fill={isSubLinkActive ? colorDefault : 'grey/light/background'}
                          size='xs'
                        />
                      )}
                      <Typography
                        variant='p1/light'
                        color={isSubLinkActive ? colorDefault : 'grey/light/background'}
                      >
                        {subLink.name}
                      </Typography>
                    </Box>
                  </Box>
                </ClaimsComponent>
              );
            })}
          </Box>
        )}
      </Box>,
      document.body,
    );
  };

  return (
    <Box mx='l' display='flex' flexDirection='column' alignItems='center'>
      <Box mb='xxl'>
        <Asset domain='images' name='logoAltermaind' height='m' />
      </Box>

      {links.map((link: ILink, index: number) => {
        const isDirectLinkActive = isPathMatch(link.url, window.location.pathname);
        const isSubLinkActive = link.subItems?.some(subItem =>
          isPathMatch(subItem.url, window.location.pathname),
        );
        const isLinkActive = isDirectLinkActive || isSubLinkActive;
        return (
          <ClaimsComponent
            key={link.key}
            featureFlag={link.ff || undefined}
            featureFlagCheckMode={link.ffMode || undefined}
            claimKey={link.claim || undefined}
            roles={link.roles || undefined}
            notCheckWriteOrRead
          >
            <Box
              position='relative'
              onMouseEnter={hasSubMenu(link) ? e => handleMouseEnter(index, e) : undefined}
              onMouseLeave={hasSubMenu(link) ? handleMouseLeave : undefined}
            >
              <Box
                data-testid={`menu-light-item-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  cursor: 'pointer',
                  ...(isLinkActive ? hoverStyle : {}),
                }}
                borderRadius='50%'
                px='xs'
                pt={16 as never}
                pb={13 as never}
                mb='xxs'
                onClick={() => {
                  if (!hasSubMenu(link)) {
                    goTo(link.url, undefined, false);
                    if (handleMenuToggle) handleMenuToggle();
                  }
                }}
              >
                {link.icon && (
                  <Svg
                    name={link.icon}
                    stroke={isLinkActive ? colorDefault : 'primary/text'}
                    fill={isLinkActive ? colorDefault : 'primary/text'}
                    size='s'
                  />
                )}
              </Box>
              {hasSubMenu(link) && renderPopover(link, index)}
            </Box>
          </ClaimsComponent>
        );
      })}

      {isChatBotEnabled && (
        <Box
          p='xs'
          borderRadius='l'
          onClick={openChatbot}
          style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
        >
          <Svg name='ai' size='s' fill='primary/text' />
        </Box>
      )}
    </Box>
  );
};
